import React from "react";
import { Typography, Box, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import colors from "../../../Constants/colors";
import { Document } from "../../../Models/company";
import { CardDoc } from "./CardDoc";
import { EmptyState } from "./EmptyState";
import { DraggableCard } from "./DraggableCard";
import { useTranslation } from "react-i18next";
import { AnnexDocument } from "../../../Core/useAnnexActions";
import { allowedExtensionsAnnex } from "../../../Constants/fileExtensions";

interface AvailableDocsListProps {
  availableDocs: Document[];
  onAddDocument: (doc: Document) => void;
  maxShown?: number;
}

export function AvailableDocsList({
  availableDocs,
  onAddDocument,
  maxShown = 12,
}: AvailableDocsListProps) {
  const { t } = useTranslation("evaluationPage");
  const [isExpanded, setIsExpanded] = React.useState(false);

  const displayedDocs = isExpanded
    ? availableDocs
    : availableDocs.slice(0, maxShown);

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{
          color: colors.gray700,
          textAlign: "right",
          mb: 2,
          fontWeight: 600,
        }}
      >
        {t("evaluationPage.availableDocuments")}
      </Typography>

      {availableDocs.length === 0 ? (
        <EmptyState text={t("evaluationPage.none")} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {displayedDocs
            .filter((d) => {
              const ext = d.name.split(".").pop()?.toLowerCase();
              return ext && allowedExtensionsAnnex.includes(ext);
            })
            .map((d) => (
              <DraggableCard key={d.id} doc={d}>
                <CardDoc
                  doc={d as unknown as AnnexDocument}
                  onClick={() => onAddDocument(d)}
                  onAddPress={() => onAddDocument(d)}
                />
              </DraggableCard>
            ))}
        </Box>
      )}

      {availableDocs.length > maxShown && (
        <Box display="flex" justifyContent="center" mt={1}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              color: colors.accent,
              "&:hover": { bgcolor: colors.gray100 },
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}
    </>
  );
}
